import type { TFunction } from 'i18next';
import { shortcutsRegistry } from './registry';
import type { Shortcut } from './types';

/**
 * Registers the default global shortcuts. Called once at app bootstrap with
 * an i18n `t()` and a navigate function.
 *
 * The `?` shortcut for opening the help sheet is registered here primarily
 * so it shows up in the sheet's own listing — the actual open is handled
 * by `HelpSheet` itself (a separate listener so the sheet stays available
 * if the registry is empty).
 */
export function seedShortcuts(
  t: TFunction,
  navigate: (path: string) => void,
): void {
  const shortcuts: Shortcut[] = [
    {
      id: 'help.open',
      keys: '?',
      description: t('shortcuts.actions.openHelp'),
      group: 'help',
      run: () => {
        // No-op; HelpSheet's own listener handles the toggle. Listed here so
        // the help sheet displays the binding.
      },
    },
    {
      id: 'nav.dashboard',
      keys: 'g d',
      description: t('shortcuts.actions.goToDashboard'),
      group: 'navigation',
      run: () => navigate('/dashboard'),
    },
    {
      id: 'nav.buildings',
      keys: 'g b',
      description: t('shortcuts.actions.goToBuildings'),
      group: 'navigation',
      run: () => navigate('/buildings'),
    },
    {
      id: 'nav.reports',
      keys: 'g r',
      description: t('shortcuts.actions.goToReports'),
      group: 'navigation',
      run: () => navigate('/analytics/reports'),
    },
    {
      id: 'nav.tasks',
      keys: 'g t',
      description: t('shortcuts.actions.goToTasks'),
      group: 'navigation',
      run: () => navigate('/notifications'),
    },
    {
      id: 'action.create-report',
      keys: 'c r',
      description: t('shortcuts.actions.createReport'),
      group: 'actions',
      run: () => navigate('/analytics/reports'),
    },
  ];

  for (const shortcut of shortcuts) {
    shortcutsRegistry.register(shortcut);
  }
}
